/**
 * The overlays the shell itself owns (spec M0-F4 §7.7): the notifications pane, the global
 * search panel, the shortcut sheet and the invite modal's opener.
 *
 * F4 shipped the **frames**; M5.C1 filled the pane and **M5.C5 (round 5m) filled the search
 * panel**, which is now the measured 1318 × 751 surface from
 * `discovery/ui-reference/search/01-focused-anatomy.json` rather than F4's empty state.
 */
import {
  AnchoredPopover,
  IconButton,
  PRODUCT_NAME,
  Search,
  SlideOverPane,
  Tab,
  Tabs,
  Toggle,
  icons,
} from '@elizmonday/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTRPCClient } from '../../lib/trpc';
import { invalidateUnreadCounts, useUnreadQueryKeys } from '../unread';
import {
  NotificationsPanel,
  createNotificationsSource,
  useNotificationsRealtime,
  type NotificationsTrpc,
} from '../../modules/notifications';
import '../../modules/notifications/notifications.css';
import { SearchPanel, createSearchSource, type SearchTrpc } from '../../modules/search';
import '../../modules/search/search.css';
import { usePreference } from '../../modules/shell/preferences';
import { useShellStore } from '../../modules/shell/store';
import { useBootstrap } from '../../modules/shell/bootstrap';
import { InviteDialog } from '../../modules/profile/InviteDialog';
import { createAdminSource } from '../../modules/admin/trpc-source';
import type { AdminTrpc } from '../../modules/admin/trpc-source';
import '../../modules/admin/admin.css';
import { ShortcutSheet } from './ShortcutSheet';

/**
 * A centred, top-anchored dialog. The **search** panel is no longer ASSUMED — it is measured
 * (§C5 §2) and positions itself; `DialogShell` supplies only the backdrop for it.
 */
function DialogShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="ez-dialog-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      data-testid="overlay-backdrop"
    >
      {children}
    </div>
  );
}

const NOTIFICATION_TABS = ['all', 'mentioned', 'assigned'] as const;

function NotificationsPane({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [unreadOnly, setUnreadOnly] = usePreference('notifications.unreadOnly');
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  // M5.C1 fills the body. The source is built once per mount so the panel's optimistic
  // updates are not thrown away by a re-render.
  const trpcClient = useTRPCClient();
  const source = useMemo(
    () => createNotificationsSource(trpcClient as unknown as NotificationsTrpc),
    [trpcClient],
  );
  const { revision } = useNotificationsRealtime(true);
  /**
   * **P-168.** Marking everything read is a mutation the *top bar* has to hear about: its badge
   * reads `notifications.unreadCount` / `inbox.unreadCount` (`app/unread.ts`), and nothing else
   * on this screen invalidates them.
   */
  const queryClient = useQueryClient();
  const unreadKeys = useUnreadQueryKeys();
  const [menuOpen, setMenuOpen] = useState(false);
  const [localRevision, setLocalRevision] = useState(0);
  /**
   * P-008. The `…` menu used to be a `position: absolute` box inside the pane, at
   * `inset-inline-end: 60px`. On a phone the pane is the whole screen and F9-D6 widens the menu
   * to `100vw − 16`, so 60 px from the right of a 374 px box put its left edge at **−44**: the
   * first third of every row was off the left of the screen, on every screen with the shell.
   * It is an `AnchoredPopover` now — measured against the button, portalled, clamped — so the
   * pane's own width can never decide where it lands again.
   */
  const menuButtonRef = useRef<HTMLSpanElement>(null);

  return (
    <SlideOverPane
      open
      onClose={onClose}
      title="Notifications"
      data-testid="notifications-pane"
      headerActions={
        <>
          <IconButton
            icon={icons.settings}
            ariaLabel="Change notification settings"
            size="xs"
            kind="tertiary"
            onClick={() => {
              onClose();
              window.location.assign('/me/notifications');
            }}
            data-testid="notifications-settings-button"
          />
          <span ref={menuButtonRef} className="ez-notif-pane-menu__anchor">
            <IconButton
              icon={icons.moreActions}
              ariaLabel="Notifications Options"
              size="xs"
              kind="tertiary"
              onClick={() => setMenuOpen((open) => !open)}
              data-testid="notifications-options-button"
            />
          </span>
        </>
      }
      toolbar={
        <>
          <Tabs activeTabId={tab} onTabChange={setTab} data-testid="notifications-tabs">
            <Tab value={0}>All</Tab>
            <Tab value={1}>Mentioned</Tab>
            <Tab value={2}>Assigned to me</Tab>
          </Tabs>
          <div className="ez-pane-filter-row">
            <Search
              placeholder="Search notifications by people, boards, and more..."
              inputAriaLabel="Search notifications"
              value={query}
              onChange={setQuery}
              data-testid="notifications-search"
            />
            <Toggle
              isSelected={unreadOnly}
              onChange={setUnreadOnly}
              aria-label="Unread only"
              data-testid="notifications-unread-only"
            />
            <span className="ez-pane-filter-row__label">Unread only</span>
          </div>
        </>
      }
    >
      <AnchoredPopover
        anchor={menuButtonRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        label="Notifications Options"
        role="menu"
        align="end"
        /* Measured: the menu sits on the button's bottom edge, 240 wide (INDEX §1 "Menus"). */
        gap={0}
        width={240}
        data-testid="notifications-pane-menu"
      >
        <div className="ez-notif-pane-menu">
          <button
            type="button"
            role="menuitem"
            data-testid="notifications-mark-all-read"
            onClick={() => {
              setMenuOpen(false);
              void source.markAllRead().then(() => {
                setLocalRevision((value) => value + 1);
                invalidateUnreadCounts(queryClient, unreadKeys);
              });
            }}
          >
            Mark all as read
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="notifications-delete-all"
            onClick={() => {
              setMenuOpen(false);
              void source.removeAll().then(() => {
                setLocalRevision((value) => value + 1);
                invalidateUnreadCounts(queryClient, unreadKeys);
              });
            }}
          >
            Delete all
          </button>
        </div>
      </AnchoredPopover>
      <NotificationsPanel
        source={source}
        tab={NOTIFICATION_TABS[tab] ?? 'all'}
        unreadOnly={unreadOnly}
        query={query}
        revision={revision + localRevision}
        /*
         * P-187. `NotificationRow` calls `onOpen?.(row)` from both its click and its Enter/Space
         * handler, and the pane used to pass none, so a row that looks and behaves like a control
         * reached nothing. The destination is the item the row is about — monday's own deep link
         * `/boards/:boardId/pulses/:itemId` `[doc 00]`, which `BoardRedirect` resolves to the
         * board's remembered view with the item card open. A row with no item falls back to the
         * board. Marking it read on the way is what the bell's own unread count implies (q540).
         */
        onOpen={(row) => {
          if (!row.read) void source.markRead([row.id], true).catch(() => undefined);
          const target = row.itemId && row.boardId
            ? `/boards/${row.boardId}/pulses/${row.itemId}`
            : row.boardId
              ? `/boards/${row.boardId}`
              : null;
          onClose();
          if (target) navigate(target);
        }}
      />
    </SlideOverPane>
  );
}

/**
 * M5.C5. F4 shipped the frame with a `PageEmptyState` saying "Results arrive with M5"; this is
 * M5. The panel owns its own surface (a fixed 1318 × 751 at (61, 117), measured), so it is
 * **not** wrapped in `DialogShell`'s centred card — only in the backdrop, which is what closes
 * it on an outside click.
 */
function GlobalSearchDialog({ onClose }: { onClose: () => void }) {
  const trpcClient = useTRPCClient();
  const navigate = useNavigate();
  const source = useMemo(() => createSearchSource(trpcClient as unknown as SearchTrpc), [trpcClient]);
  return (
    <DialogShell onClose={onClose}>
      <SearchPanel source={source} onClose={onClose} onNavigate={(href) => navigate(href)} />
    </DialogShell>
  );
}

/**
 * B10: this was a `PageEmptyState` reading "Inviting people arrives with the admin module"
 * long after M8.P2 shipped `admin.invitations.invite` **and** the dialog that drives it. The
 * top bar's `Invite members` (`[c:topbar-invite]`, a 620 × 471 modal with `Copy invite link`
 * and `Send invite`) now opens the real one.
 */
function InviteModal({ onClose }: { onClose: () => void }) {
  const trpcClient = useTRPCClient();
  const source = useMemo(() => createAdminSource(trpcClient as unknown as AdminTrpc), [trpcClient]);
  const bootstrap = useBootstrap();
  const canInviteAdmins = bootstrap.data?.capabilities.administration ?? false;

  return (
    <DialogShell onClose={onClose}>
      <div onClick={(event) => event.stopPropagation()} data-testid="invite-modal">
        <InviteDialog
          source={source}
          productName={PRODUCT_NAME}
          canInviteAdmins={canInviteAdmins}
          onClose={onClose}
        />
      </div>
    </DialogShell>
  );
}

/** One place that renders whichever shell overlay is open. Never more than one. */
export function ShellOverlays() {
  const overlay = useShellStore((state) => state.overlay);
  const close = useShellStore((state) => state.closeOverlay);

  switch (overlay) {
    case 'notifications':
      return <NotificationsPane onClose={close} />;
    case 'search':
      return <GlobalSearchDialog onClose={close} />;
    case 'invite':
      return <InviteModal onClose={close} />;
    case 'shortcuts':
      return (
        <DialogShell onClose={close}>
          <ShortcutSheet />
        </DialogShell>
      );
    default:
      return null;
  }
}
